import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import {
    Box,
    CircularProgress,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Tooltip
} from "@mui/material";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {RefObject, useState} from "react";
import SurveySubmissionPrintable from "./export/SurveySubmissionPrintable";
import type {
    SurveySubmissionDocumentData,
    SurveySubmissionDocumentFormat
} from "./export/surveySubmissionDocument";

interface SurveySubmissionExportActionsProps {
    busyFormat: SurveySubmissionDocumentFormat | null;
    onPrint: () => void;
    onExport: (format: SurveySubmissionDocumentFormat) => void;
}

/** Print and Download beside the survey's name; the download menu is this component's own. */
const SurveySubmissionExportActions = ({busyFormat, onPrint, onExport}: SurveySubmissionExportActionsProps) => {
    const {t} = useLocalize();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const exportAs = (format: SurveySubmissionDocumentFormat) => {
        setAnchorEl(null);
        onExport(format);
    };

    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            gap={0.25}
            sx={{flexShrink: 0}}
        >
            <Tooltip title={t('Print')}>
                <IconButton
                    onClick={onPrint}
                    aria-label={t('Print')}
                    sx={{width: 40, height: 40}}
                >
                    <PrintIcon/>
                </IconButton>
            </Tooltip>

            <Tooltip title={t('Download')}>
                <IconButton
                    onClick={event => setAnchorEl(event.currentTarget)}
                    aria-label={t('Download')}
                    disabled={busyFormat !== null}
                    sx={{width: 40, height: 40}}
                >
                    {busyFormat !== null
                        ? <CircularProgress size={18}/>
                        : <FileDownloadOutlinedIcon/>}
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <MenuItem onClick={() => exportAs('pdf')}>
                    <ListItemIcon>
                        <PictureAsPdfIcon fontSize={'small'}/>
                    </ListItemIcon>
                    <ListItemText>{t('Download PDF')}</ListItemText>
                </MenuItem>

                <MenuItem onClick={() => exportAs('docx')}>
                    <ListItemIcon>
                        <DescriptionOutlinedIcon fontSize={'small'}/>
                    </ListItemIcon>
                    <ListItemText>{t('Download Word')}</ListItemText>
                </MenuItem>
            </Menu>
        </Stack>
    );
};

/** Off-screen source for Print: the same document the PDF and Word exports render. */
export function SurveySubmissionPrintSource({printRef, data}: {
    printRef: RefObject<HTMLDivElement>;
    data: SurveySubmissionDocumentData;
}) {
    return (
        <Box
            aria-hidden
            sx={{
                position: 'absolute',
                top: 0,
                left: -100000,
                /* A4 content width at the PDF's 40pt side margins, so the hidden
                   render measures the same as the printed page. */
                width: '515pt',
                pointerEvents: 'none',
            }}
        >
            <SurveySubmissionPrintable ref={printRef} data={data}/>
        </Box>
    );
}

export default SurveySubmissionExportActions;
